import { useState, useEffect } from 'react';
import { Inbox, Check, X, Trash2, ExternalLink, Loader2, Clock, AlertCircle, Image as ImageIcon, FileText, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { API, toFullUrl } from '../../config/api';

export function ApplicationsPanel() {
  const [view, setView] = useState('applications'); // 'applications' | 'photos'
  const [apps, setApps] = useState([]);
  const [photoSubs, setPhotoSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [denyDialog, setDenyDialog] = useState(null);
  const [photoDenyDialog, setPhotoDenyDialog] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      if (view === 'applications') {
        const { data } = await api.get(`${API}/featured-artists/admin/applications`, { params });
        setApps(data);
      } else {
        const { data } = await api.get(`${API}/featured-artists/admin/photo-submissions`, { params });
        setPhotoSubs(data);
      }
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filter, view]);

  const approve = async (app) => {
    if (!window.confirm(`Approve ${app.name}? They'll get an email with their access code.`)) return;
    try {
      const { data } = await api.post(`${API}/featured-artists/admin/applications/${app.id}/approve`);
      toast.success(`Approved! Access code: ${data.access_code}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    }
  };

  const deny = async () => {
    if (!denyDialog) return;
    try {
      await api.post(`${API}/featured-artists/admin/applications/${denyDialog.id}/deny`,
        { admin_note: denyDialog.note });
      toast.success('Application denied');
      setDenyDialog(null);
      fetchData();
    } catch (err) {
      toast.error('Denial failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this application?')) return;
    try {
      await api.delete(`${API}/featured-artists/admin/applications/${id}`);
      fetchData();
    } catch (err) { toast.error('Delete failed'); }
  };

  // Photo submission handlers
  const approvePhoto = async (sub) => {
    if (!window.confirm(`Approve ${sub.artist_name}'s ${sub.kind} submission?`)) return;
    try {
      await api.post(`${API}/featured-artists/admin/photo-submissions/${sub.id}/approve`);
      toast.success(`${sub.kind} approved`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
  };
  const denyPhoto = async () => {
    if (!photoDenyDialog) return;
    try {
      await api.post(`${API}/featured-artists/admin/photo-submissions/${photoDenyDialog.id}/reject`,
        { admin_note: photoDenyDialog.note });
      toast.success('Submission rejected');
      setPhotoDenyDialog(null);
      fetchData();
    } catch (err) { toast.error('Rejection failed'); }
  };
  const deletePhoto = async (id) => {
    if (!window.confirm('Permanently delete this submission?')) return;
    try {
      await api.delete(`${API}/featured-artists/admin/photo-submissions/${id}`);
      fetchData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const statusColors = {
    pending: 'border-[#DAA520]/40 text-[#DAA520]',
    approved: 'border-[#22C55E]/40 text-[#22C55E]',
    denied: 'border-[#FF3B30]/40 text-[#FF3B30]',
  };

  return (
    <div className="space-y-6" data-testid="applications-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
          <Inbox className="w-6 h-6 text-[#DAA520]" /> APPROVALS
        </h2>
        <div className="flex items-center gap-1 bg-[#141414] border border-[#262626] p-1">
          {['pending', 'approved', 'denied', 'all'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${filter === s ? 'bg-[#DAA520] text-black' : 'text-[#A3A3A3] hover:text-white'}`}
              data-testid={`apps-filter-${s}`}>
              {s === 'denied' && view === 'photos' ? 'rejected' : s}
            </button>
          ))}
        </div>
      </div>

      {/* View switcher */}
      <div className="flex items-center gap-2 border-b border-[#262626]">
        <button onClick={() => setView('applications')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${view === 'applications' ? 'border-[#DAA520] text-[#DAA520]' : 'border-transparent text-[#A3A3A3] hover:text-white'}`}
          data-testid="view-applications">
          <Inbox className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Sub-Page Applications
        </button>
        <button onClick={() => setView('photos')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${view === 'photos' ? 'border-[#DAA520] text-[#DAA520]' : 'border-transparent text-[#A3A3A3] hover:text-white'}`}
          data-testid="view-photos">
          <ImageIcon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Photo Submissions
        </button>
        <button onClick={() => setView('templates')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${view === 'templates' ? 'border-[#DAA520] text-[#DAA520]' : 'border-transparent text-[#A3A3A3] hover:text-white'}`}
          data-testid="view-templates">
          <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Templates
        </button>
      </div>

      {view === 'templates' && <TemplatesView />}
      {view !== 'templates' && (loading ? (
        <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : view === 'applications' ? (
        apps.length === 0 ? (
        <div className="py-16 text-center bg-[#141414] border border-[#262626]">
          <Inbox className="w-12 h-12 text-[#262626] mx-auto mb-3" />
          <p className="text-sm text-[#737373]">No {filter !== 'all' ? filter : ''} applications.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map(app => (
            <div key={app.id} className={`bg-[#141414] border ${statusColors[app.status] || 'border-[#262626]'} p-4`} data-testid={`app-card-${app.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-bold text-lg">{app.name}</h3>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 border ${statusColors[app.status]}`}>{app.status}</span>
                  </div>
                  <p className="text-xs text-[#A3A3A3] font-mono">{app.email}</p>
                  <p className="text-[10px] text-[#525252] mt-0.5">slug: <span className="font-mono text-[#DAA520]">{app.slug_suggestion}</span> · {new Date(app.created_at).toLocaleString()}</p>
                  {app.bio && <p className="text-sm text-[#D4D4D4] mt-3 leading-relaxed whitespace-pre-line">{app.bio}</p>}
                  {app.why_apply && (
                    <div className="mt-3 bg-[#0A0A0A] border-l-2 border-l-[#DAA520] px-3 py-2">
                      <p className="text-[9px] uppercase tracking-wider text-[#DAA520] font-bold mb-1">Why MeloDrop</p>
                      <p className="text-sm text-[#D4D4D4] italic">{app.why_apply}</p>
                    </div>
                  )}
                  {app.admin_note && (
                    <div className="mt-3 bg-[#1F0A0A] border-l-2 border-l-[#FF3B30] px-3 py-2">
                      <p className="text-[9px] uppercase tracking-wider text-[#FF3B30] font-bold mb-1">Denial Reason</p>
                      <p className="text-sm text-[#D4D4D4] italic">{app.admin_note}</p>
                    </div>
                  )}
                  {/* Links */}
                  {app.links && Object.values(app.links).some(Boolean) && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {Object.entries(app.links).map(([platform, url]) => url ? (
                        <a key={platform} href={url} target="_blank" rel="noreferrer"
                          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520] flex items-center gap-1">
                          <ExternalLink className="w-2.5 h-2.5" /> {platform}
                        </a>
                      ) : null)}
                      {app.sample_track_url && (
                        <a href={app.sample_track_url} target="_blank" rel="noreferrer"
                          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#DAA520]/40 text-[#DAA520] hover:bg-[#DAA520]/10 flex items-center gap-1">
                          <ExternalLink className="w-2.5 h-2.5" /> Sample Track
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {app.status === 'pending' && (
                    <>
                      <button onClick={() => approve(app)}
                        className="px-3 py-2 bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] text-black text-xs font-bold uppercase flex items-center gap-1.5 transition-colors"
                        data-testid={`approve-app-${app.id}`}>
                        <Check className="w-3.5 h-3.5" /> APPROVE
                      </button>
                      <button onClick={() => setDenyDialog({ id: app.id, name: app.name, note: '' })}
                        className="px-3 py-2 border border-[#FF3B30]/50 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] text-xs font-bold uppercase flex items-center gap-1.5 transition-colors"
                        data-testid={`deny-app-${app.id}`}>
                        <X className="w-3.5 h-3.5" /> DENY
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDelete(app.id)}
                    className="px-2 py-1.5 border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#A3A3A3]"
                    title="Delete record" data-testid={`delete-app-${app.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )
      ) : (
        photoSubs.length === 0 ? (
          <div className="py-16 text-center bg-[#141414] border border-[#262626]">
            <ImageIcon className="w-12 h-12 text-[#262626] mx-auto mb-3" />
            <p className="text-sm text-[#737373]">No {filter !== 'all' ? filter : ''} photo submissions.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="photo-subs-grid">
            {photoSubs.map(sub => (
              <div key={sub.id} className="bg-[#141414] border border-[#262626] overflow-hidden" data-testid={`photo-sub-${sub.id}`}>
                <div className="aspect-video bg-[#0A0A0A] overflow-hidden">
                  <img src={toFullUrl(sub.file_url)} alt={`${sub.artist_name} ${sub.kind}`} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-bold text-sm">{sub.artist_name}</p>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 border ${
                      sub.status === 'pending' ? 'border-[#DAA520]/40 text-[#DAA520]' :
                      sub.status === 'approved' ? 'border-[#22C55E]/40 text-[#22C55E]' :
                      'border-[#FF3B30]/40 text-[#FF3B30]'}`}>{sub.status}</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-[#DAA520] font-bold">{sub.kind}</p>
                  <p className="text-[10px] text-[#525252] mt-0.5">{new Date(sub.created_at).toLocaleString()}</p>
                  {sub.admin_note && (
                    <div className="mt-2 bg-[#1F0A0A] border-l-2 border-l-[#FF3B30] px-2 py-1.5">
                      <p className="text-[9px] uppercase text-[#FF3B30] font-bold">Note</p>
                      <p className="text-[11px] text-[#D4D4D4] italic">{sub.admin_note}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-3">
                    {sub.status === 'pending' && (
                      <>
                        <button onClick={() => approvePhoto(sub)}
                          className="flex-1 px-2 py-1.5 bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] text-black text-[10px] font-bold uppercase flex items-center justify-center gap-1"
                          data-testid={`approve-photo-${sub.id}`}>
                          <Check className="w-3 h-3" /> APPROVE
                        </button>
                        <button onClick={() => setPhotoDenyDialog({ id: sub.id, kind: sub.kind, note: '' })}
                          className="flex-1 px-2 py-1.5 border border-[#FF3B30]/50 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] text-[10px] font-bold uppercase flex items-center justify-center gap-1"
                          data-testid={`reject-photo-${sub.id}`}>
                          <X className="w-3 h-3" /> REJECT
                        </button>
                      </>
                    )}
                    <button onClick={() => deletePhoto(sub.id)}
                      className="px-1.5 py-1.5 border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#A3A3A3]"
                      title="Delete" data-testid={`delete-photo-${sub.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ))}

      {/* Photo deny dialog */}
      {photoDenyDialog && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={() => setPhotoDenyDialog(null)}>
          <div className="bg-[#141414] border-2 border-[#FF3B30]/40 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="photo-deny-dialog">
            <h3 className="font-cabinet font-black uppercase text-lg mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#FF3B30]" /> REJECT {photoDenyDialog.kind.toUpperCase()}
            </h3>
            <p className="text-sm text-[#A3A3A3] mb-4">Optional reason — this will be emailed to the artist.</p>
            <textarea
              value={photoDenyDialog.note}
              onChange={(e) => setPhotoDenyDialog({ ...photoDenyDialog, note: e.target.value })}
              rows={3} maxLength={500} autoFocus
              placeholder="e.g. Image is too low resolution; please re-submit at 1500×1500px+."
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF3B30] outline-none px-3 py-2 text-sm resize-none"
              data-testid="photo-deny-note-input"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPhotoDenyDialog(null)}
                className="flex-1 px-3 py-2 border border-[#262626] hover:bg-[#1A1A1A] text-sm font-bold uppercase">Cancel</button>
              <button onClick={denyPhoto}
                className="flex-1 px-3 py-2 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] text-white text-sm font-black uppercase"
                data-testid="photo-deny-confirm-btn">Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Deny dialog */}
      {denyDialog && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={() => setDenyDialog(null)}>
          <div className="bg-[#141414] border-2 border-[#FF3B30]/40 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="deny-dialog">
            <h3 className="font-cabinet font-black uppercase text-lg mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#FF3B30]" /> DENY APPLICATION
            </h3>
            <p className="text-sm text-[#A3A3A3] mb-4">Add a brief reason — this will be emailed to <span className="text-white">{denyDialog.name}</span>.</p>
            <textarea
              value={denyDialog.note}
              onChange={(e) => setDenyDialog({ ...denyDialog, note: e.target.value })}
              rows={4} maxLength={500} autoFocus
              placeholder="e.g. We're not currently accepting artists in your genre. You're welcome to reapply in 6 months."
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF3B30] outline-none px-3 py-2 text-sm resize-none"
              data-testid="deny-note-input"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setDenyDialog(null)}
                className="flex-1 px-3 py-2 border border-[#262626] hover:bg-[#1A1A1A] text-sm font-bold uppercase">
                Cancel
              </button>
              <button onClick={deny}
                className="flex-1 px-3 py-2 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] text-white text-sm font-black uppercase"
                data-testid="deny-confirm-btn">
                Send Denial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ───────────────────────── Templates editor ─────────────────────────
const TEMPLATE_LABELS = {
  'subpage': 'Sub-Page Application',
  'track-upload': 'Track Upload',
  'image-upload': 'Image Upload',
};

function TemplatesView() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template doc

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/templates`);
      setTemplates(data.templates || []);
    } catch {
      toast.error('Failed to load templates');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      const { kind, ...patch } = editing;
      const { data } = await api.put(`${API}/featured-artists/admin/templates/${kind}`, patch);
      toast.success(`Saved · ${TEMPLATE_LABELS[kind]}`);
      setEditing(null);
      setTemplates(prev => prev.map(t => t.kind === kind ? data : t));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const reset = async (kind) => {
    if (!window.confirm(`Reset "${TEMPLATE_LABELS[kind]}" template to its default text?`)) return;
    try {
      const { data } = await api.post(`${API}/featured-artists/admin/templates/${kind}/reset`);
      toast.success('Template reset to defaults');
      setTemplates(prev => prev.map(t => t.kind === kind ? data : t));
      if (editing && editing.kind === kind) setEditing(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading templates…</div>;
  }

  return (
    <div className="space-y-4" data-testid="templates-content">
      <p className="text-sm text-[#A3A3A3]">
        Pre-loaded copy for the 3 public application pages. Edit the title, intro, success message, and the auto-emails sent on approve/deny.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {templates.map(t => (
          <div key={t.kind} className="bg-[#141414] border border-[#262626] p-5 space-y-3" data-testid={`template-card-${t.kind}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#DAA520] font-bold">{TEMPLATE_LABELS[t.kind]}</p>
                <h3 className="font-cabinet text-xl font-black uppercase leading-tight">{t.title}</h3>
              </div>
              <button onClick={() => reset(t.kind)} title="Reset to default"
                className="w-7 h-7 flex items-center justify-center text-[#666] hover:text-[#FF3B30] transition-colors"
                data-testid={`template-reset-${t.kind}`}>
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-[#A3A3A3] line-clamp-3 leading-relaxed">{t.intro}</p>
            <div className="text-[10px] text-[#666] uppercase tracking-wider space-y-1">
              <p>Fields: <span className="text-[#A3A3A3]">{(t.fields || []).join(', ') || '—'}</span></p>
              <p>Submit button: <span className="text-[#A3A3A3]">{t.submit_label}</span></p>
            </div>
            <button onClick={() => setEditing({ ...t })}
              className="w-full px-3 py-2 bg-[#DAA520] hover:bg-white text-black text-xs font-black uppercase tracking-wider transition-colors"
              data-testid={`template-edit-${t.kind}`}>
              EDIT TEMPLATE
            </button>
          </div>
        ))}
      </div>

      {/* Edit dialog */}
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setEditing(null)}>
          <div className="relative bg-[#141414] border-2 border-[#DAA520]/40 max-w-2xl w-full p-6 my-8 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="template-edit-dialog">
            <button onClick={() => setEditing(null)} className="absolute top-3 right-3 w-8 h-8 bg-[#0A0A0A] hover:bg-[#FF3B30] text-white flex items-center justify-center"
              data-testid="template-edit-close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-cabinet font-black uppercase text-xl mb-2">
              Edit · <span className="text-[#DAA520]">{TEMPLATE_LABELS[editing.kind]}</span>
            </h3>
            <TemplateField label="Page Title" value={editing.title}
              onChange={(v) => setEditing({ ...editing, title: v })} testid="template-title" />
            <TemplateField label="Intro Paragraph" value={editing.intro} rows={3}
              onChange={(v) => setEditing({ ...editing, intro: v })} testid="template-intro" />
            <TemplateField label="Submit Button Label" value={editing.submit_label}
              onChange={(v) => setEditing({ ...editing, submit_label: v })} testid="template-submit-label" />
            <TemplateField label="Success Message (after submit)" value={editing.success_message} rows={2}
              onChange={(v) => setEditing({ ...editing, success_message: v })} testid="template-success" />
            <div className="border-t border-[#262626] pt-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#22C55E] font-bold mb-2">APPROVE EMAIL</p>
              <TemplateField label="Subject" value={editing.approve_email_subject}
                onChange={(v) => setEditing({ ...editing, approve_email_subject: v })} testid="template-approve-subject" />
              <TemplateField label="Body" value={editing.approve_email_body} rows={5}
                onChange={(v) => setEditing({ ...editing, approve_email_body: v })} testid="template-approve-body" />
              <p className="text-[10px] text-[#525252] mt-1">Variables: <code className="text-[#DAA520]">{'{name}'}</code> <code className="text-[#DAA520]">{'{email}'}</code> <code className="text-[#DAA520]">{'{access_code}'}</code> <code className="text-[#DAA520]">{'{portal_url}'}</code> <code className="text-[#DAA520]">{'{title}'}</code></p>
            </div>
            <div className="border-t border-[#262626] pt-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#FF3B30] font-bold mb-2">DENY EMAIL</p>
              <TemplateField label="Subject" value={editing.deny_email_subject}
                onChange={(v) => setEditing({ ...editing, deny_email_subject: v })} testid="template-deny-subject" />
              <TemplateField label="Body" value={editing.deny_email_body} rows={5}
                onChange={(v) => setEditing({ ...editing, deny_email_body: v })} testid="template-deny-body" />
              <p className="text-[10px] text-[#525252] mt-1">Variables: <code className="text-[#FF3B30]">{'{name}'}</code> <code className="text-[#FF3B30]">{'{admin_note}'}</code></p>
            </div>
            <div className="flex gap-2 pt-3">
              <button onClick={() => setEditing(null)}
                className="flex-1 px-3 py-2 border border-[#262626] hover:bg-[#1A1A1A] text-sm font-bold uppercase">
                Cancel
              </button>
              <button onClick={save}
                className="flex-1 px-3 py-2 bg-[#DAA520] hover:bg-white hover:text-black text-black text-sm font-black uppercase"
                data-testid="template-save-btn">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateField({ label, value, onChange, rows, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold block mb-1">{label}</label>
      {rows ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm resize-y"
          data-testid={testid} />
      ) : (
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm"
          data-testid={testid} />
      )}
    </div>
  );
}
